/**
 * Places World Cup knockout ties into their BRACKET SLOTS for the mirrored tree renderer.
 *
 * The shared bracket is purely positional: `mirrorBracket` splits each round's `matches[]` down
 * the middle (first half → left side, second half → right side) and the connectors join adjacent
 * pairs `(2i, 2i+1)` into child `i`. So the slot (index) a tie sits at is the single source of
 * truth for which games connect and which side they land on.
 *
 * Real fixtures arrive in kickoff order, which is unrelated to bracket position — that is what
 * makes the tree connect the wrong games and mirror teams onto the wrong side. Two mechanisms fix
 * it, applied outward (Round of 32 → Final):
 *
 *   1. Round-of-32 anchor. R32 has no knockout parent to link from, so each tie is placed into its
 *      official slot from `WORLD_CUP_R32_SLOT_SEEDS`, keyed by its group winner (or runner-up pair).
 *   2. Team-linkage. In every later round a match's two teams are the winners of two matches in the
 *      previous (already-slotted) round, so it belongs at child `i` when those parents sit at slots
 *      `(2i, 2i+1)`.
 *
 * Slotting is per-match and returns a fixed-size array with `null` for empty/undrawn slots, so a
 * partially-drawn round (e.g. the first Round-of-16 games as their feeders finish) can be overlaid
 * onto the skeleton without waiting for the whole round. A round is only slotted when EVERY tie it
 * holds can be placed cleanly; otherwise it is left unslotted (callers fall back to kickoff order /
 * skeleton placeholders) so a guess never seats a game on the wrong side.
 */

import type { BracketMatch, BracketRound } from '../../knockout/types/knockoutBracket.types'
import {
  WORLD_CUP_R32_SLOT_SEEDS,
  WORLD_CUP_R32_SLOT_COUNT,
} from '../constants/worldCupKnockoutBracket.constants'

/** `countryCode → { groupLetter, position }` for every team, from `buildGroupPositionMap`. */
export type GroupPositionMap = Record<string, { groupLetter: string; position: number }>

/** A round's slot array: index = bracket slot, `null` = empty/undrawn slot. */
export type SlotArray = (BracketMatch | null)[]

/** Canonical key for a runner-up-vs-runner-up tie, order-independent (e.g. "A|B"). */
function runnerUpPairKey(groupA: string, groupB: string): string {
  return [groupA, groupB].sort().join('|')
}

/**
 * Slot lookups derived once from the official bracket structure:
 * - a group WINNER uniquely fixes 12 of the 16 slots (`WINNER_SLOT`);
 * - the four runner-up-vs-runner-up ties are fixed by their group pair (`RUNNER_UP_PAIR_SLOT`).
 */
const WINNER_SLOT: Record<string, number> = {}
const RUNNER_UP_PAIR_SLOT: Record<string, number> = {}
WORLD_CUP_R32_SLOT_SEEDS.forEach((seed, slot) => {
  for (const side of [seed.home, seed.away]) {
    if (side.kind === 'winner') WINNER_SLOT[side.group] = slot
  }
  if (seed.home.kind === 'runnerUp' && seed.away.kind === 'runnerUp') {
    RUNNER_UP_PAIR_SLOT[runnerUpPairKey(seed.home.group, seed.away.group)] = slot
  }
})

function seedOf(
  slot: BracketMatch['home'],
  gpm: GroupPositionMap,
): { groupLetter: string; position: number } | undefined {
  const code = slot.countryCode
  return code ? gpm[code] : undefined
}

/**
 * The bracket slot a Round-of-32 tie belongs to: keyed by its group winner where the tie has one
 * (W-vs-3rd and W-vs-RU), else by the runner-up pair (RU-vs-RU). Null when the seeds are unknown
 * or the tie doesn't match the official structure.
 */
function slotForRoundOf32(match: BracketMatch, gpm: GroupPositionMap): number | null {
  const home = seedOf(match.home, gpm)
  const away = seedOf(match.away, gpm)
  if (home?.position === 1) return WINNER_SLOT[home.groupLetter] ?? null
  if (away?.position === 1) return WINNER_SLOT[away.groupLetter] ?? null
  if (home?.position === 2 && away?.position === 2) {
    return RUNNER_UP_PAIR_SLOT[runnerUpPairKey(home.groupLetter, away.groupLetter)] ?? null
  }
  return null
}

/** Country code of the side that won the tie, or null when undecided / not yet played. */
function winnerCountryCode(match: BracketMatch): string | null {
  if (match.home.isWinner && match.home.countryCode) return match.home.countryCode
  if (match.away.isWinner && match.away.countryCode) return match.away.countryCode
  return null
}

/** Compact a slot array (drop empty slots) preserving slot order. */
function compact(slots: SlotArray): BracketMatch[] {
  return slots.filter((m): m is BracketMatch => m != null)
}

/**
 * Slot the Round of 32 (size 16) via `slotForRoundOf32`. Returns null (round left unslotted) when
 * the group map is empty or any tie can't be cleanly placed — a partial round with all-placeable
 * ties still slots, leaving empty slots `null`.
 */
function slotRoundOf32(matches: BracketMatch[], gpm: GroupPositionMap): SlotArray | null {
  if (Object.keys(gpm).length === 0) return null
  const slots: SlotArray = new Array(WORLD_CUP_R32_SLOT_COUNT).fill(null)
  for (const match of matches) {
    const slot = slotForRoundOf32(match, gpm)
    if (slot == null || slot < 0 || slot >= WORLD_CUP_R32_SLOT_COUNT || slots[slot] != null) return null
    slots[slot] = match
  }
  return slots
}

/**
 * Slot a post-R32 round by linkage to the previous round's slot array: each match's teams are the
 * winners of a parent pair at slots (2i, 2i+1), so the match belongs at child slot i. Returns null
 * if any tie can't be linked or a pairing is inconsistent (the previous round wasn't true bracket
 * order). Empty child slots stay `null`.
 */
function slotRoundByLinkage(matches: BracketMatch[], previousSlots: SlotArray): SlotArray | null {
  const childCount = Math.floor(previousSlots.length / 2)
  if (childCount < 1) return null

  const parentSlotByWinner = new Map<string, number>()
  previousSlots.forEach((parent, index) => {
    if (!parent) return
    const code = winnerCountryCode(parent)
    if (code) parentSlotByWinner.set(code, index)
  })

  const slots: SlotArray = new Array(childCount).fill(null)
  for (const match of matches) {
    const homeParent = match.home.countryCode
      ? parentSlotByWinner.get(match.home.countryCode)
      : undefined
    const awayParent = match.away.countryCode
      ? parentSlotByWinner.get(match.away.countryCode)
      : undefined

    const linked = homeParent ?? awayParent
    if (linked == null) return null
    // Both sides must descend from the same parent pair, else the previous round wasn't in true
    // bracket order — bail rather than seat the match on a guessed side.
    if (homeParent != null && awayParent != null && Math.floor(homeParent / 2) !== Math.floor(awayParent / 2)) {
      return null
    }
    const child = Math.floor(linked / 2)
    if (child < 0 || child >= childCount || slots[child] != null) return null
    slots[child] = match
  }
  return slots
}

/** Slot one round given the previous round's slot array (null for the outermost / unslotted). */
function slotRound(
  round: BracketRound,
  gpm: GroupPositionMap,
  previousSlots: SlotArray | null,
): SlotArray | null {
  if (round.key === 'round_of_32') return slotRoundOf32(round.matches, gpm)
  if (previousSlots) return slotRoundByLinkage(round.matches, previousSlots)
  return null
}

/**
 * Slot every knockout round (outer → inner) into fixed-size slot arrays keyed by round. Only rounds
 * whose ties all placed cleanly are included; a failure also stops linkage from propagating into
 * deeper rounds. Used to overlay real ties onto the full skeleton per-slot (supports partial rounds).
 */
export function computeKnockoutSlotsByKey(
  rounds: BracketRound[],
  gpm: GroupPositionMap,
): Map<string, SlotArray> {
  const byKey = new Map<string, SlotArray>()
  let previousSlots: SlotArray | null = null
  for (const round of rounds) {
    const slots = slotRound(round, gpm, previousSlots)
    if (slots) byKey.set(round.key, slots)
    previousSlots = slots
  }
  return byKey
}

/**
 * Reorder built knockout rounds (outer → inner) into bracket-slot order, dropping empty slots.
 * Rounds that can't be slotted keep their input (kickoff) order. Used by `buildWorldCupBracket`
 * for the standalone real-only model; the tab renders via `computeKnockoutSlotsByKey` + overlay.
 */
export function orderKnockoutRounds(
  rounds: BracketRound[],
  gpm: GroupPositionMap,
): BracketRound[] {
  let previousSlots: SlotArray | null = null
  return rounds.map((round) => {
    const slots = slotRound(round, gpm, previousSlots)
    previousSlots = slots
    return slots ? { ...round, matches: compact(slots) } : round
  })
}
